import {
  Box,
  Paper,
  styled,
  Typography,
  Modal,
  TextField,
  Button,
} from '@mui/material';
import Fade from '@mui/material/Fade';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import { store } from '../store';
import {
  cleanImponibleState,
  createImponibleByCuit,
  errorImponible,
  messageImponible,
  statusImponible,
} from '../features/imponibles/imponiblesSlice';
import { taxTypes } from '../utils/hardcoded';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useSelector } from 'react-redux';
import CustomAlert from './CustomAlert';
import { useEffect } from 'react';
import { getUnreadNotNumber } from '../features/notificaciones/notificacionesSlice';
import { getLabelClaveComprobante } from '../utils';

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 300,
  boxShadow: 24,
  padding: '2rem 2rem 2rem 2rem',
};

const ModalWrapper = styled(Paper)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '1rem 2rem 1rem 1.5rem',
  justifyContent: 'space-between',
  border: '2px solid',
  borderColor: theme.palette.primary.main,
}));

interface AddImponibleProps {
  openModal: boolean;
  setOpenModal: (v: boolean) => void;
}

export default function AddImponible(props: AddImponibleProps) {
  const { openModal, setOpenModal } = props;

  const errorImp = useSelector(errorImponible);
  const statusImp = useSelector(statusImponible);
  const messageImp = useSelector(messageImponible);

  const addImponibleValidationSchema = yup.object().shape({
    tipo: yup
      .string()
      .test(
        'is-required',
        'Imponible es requerido',
        value => value !== undefined
      ),
    id: yup
      .string()
      .when('tipo', {
        is: 'INM',
        then: yup.string().required('El campo partida es obligatorio'),
      })
      .when('tipo', {
        is: 'CEM',
        then: yup.string().required('El campo número de nicho es obligatorio'),
      })
      .when('tipo', {
        is: 'ROD',
        then: yup.string().required('El campo dominio es obligatorio'),
      })
      .when('tipo', {
        is: 'COM',
        then: yup.string().required('El campo número de comercio es obligatorio'),
      })
      .when('tipo', {
        is: 'CON',
        then: yup
          .string()
          .length(11, 'El Cuit debe ser de 11 caracteres')
          .required('El campo CUIT/CUIL es obligatorio'),
      }),

    ref: yup.string().required('El campo referencia es obligatorio'),
  });

  useEffect(() => {
    store.dispatch(cleanImponibleState());
  }, [openModal]);

  return (
    <div>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openModal}
        onClose={() => setOpenModal(!openModal)}
        closeAfterTransition
      >
        <Fade in={openModal}>
          <ModalWrapper sx={style}>
            <Typography
              id="transition-modal-title"
              variant="h6"
              component="h2"
              mb={4}
            >
              Agregar Imponible
            </Typography>

            <Formik
              initialValues={{
                tipo: '',
                id: '',
                ref: '',
              }}
              validationSchema={addImponibleValidationSchema}
              onSubmit={values => {
                const newValues = { ...values };

                if (newValues.tipo === 'CON') {
                  newValues.id =
                    newValues.id.slice(0, 2) +
                    '-' +
                    newValues.id.slice(2, 10) +
                    '-' +
                    newValues.id.slice(10, 12);
                }

                store.dispatch(createImponibleByCuit(newValues))
                  .unwrap()
                  .then(() => store.dispatch(getUnreadNotNumber()))
                  .then(() => setOpenModal(!openModal));
              }}
            >
              {({
                handleChange,
                handleSubmit,
                setFieldValue,
                values,
                errors,
                touched,
                resetForm,
              }) => (
                <>
                  <FormControl fullWidth>
                    <TextField
                      select
                      id="taxable"
                      label="Imponible"
                      value={values.tipo}
                      onChange={handleChange('tipo')}
                      sx={{ marginBottom: '1rem' }}
                      error={touched.tipo && Boolean(errors.tipo)}
                      helperText={touched.tipo && errors.tipo}
                    >
                      {taxTypes.map((tax, i) => (
                        <MenuItem key={i} value={tax.value}>
                          {tax.name}
                        </MenuItem>
                      ))}
                    </TextField>

                    {
                      values.tipo !== ''
                        ? <TextField
                            variant="outlined"
                            id="taxabletype"
                            value={values.id}
                            onChange={e => {
                              setFieldValue(
                                'id',
                                e.target.value.toLocaleUpperCase()
                              );
                            }}
                            sx={{ marginBottom: '1rem' }}
                            error={touched.id && Boolean(errors.id)}
                            helperText={touched.id && errors.id}
                            label={ getLabelClaveComprobante(values.tipo) }
                          />
                        : null
                    }
                    <TextField
                      variant="outlined"
                      id="reference"
                      label={'Referencia'}
                      value={values.ref}
                      onChange={handleChange('ref')}
                      sx={{ marginBottom: '1rem' }}
                      error={touched.ref && Boolean(errors.ref)}
                      helperText={touched.ref && errors.ref}
                      placeholder="Ej: Casa Zulma, Moto Pepe"
                    />
                  </FormControl>

                  <Box sx={{ display: 'flex', width: '100%' }}>
                    <Button
                      variant="contained"
                      onClick={() => handleSubmit()}
                      sx={{
                        color: 'white',
                        width: '100%',
                        marginRight: '1rem',
                      }}
                    >
                      Suscribir
                    </Button>
                    <Button
                      variant="outlined"
                      sx={{ width: '100%' }}
                      onClick={() => {
                        resetForm();
                        store.dispatch(cleanImponibleState());
                        setOpenModal(!openModal);
                      }}
                    >
                      Cancelar
                    </Button>
                  </Box>
                  <CustomAlert
                    status={statusImp}
                    message={messageImp}
                    errorMessage={errorImp}
                    style={{ marginTop: '1rem' }}
                  />
                </>
              )}
            </Formik>
          </ModalWrapper>
        </Fade>
      </Modal>
    </div>
  );
}
